import { useToastStore } from '@/features/toasts/stores/useToastStore';
import { useBackendApi } from '@/services/useBackendApi';
import { useMutation, useQuery, useQueryCache, type UseMutationOptions } from '@pinia/colada';
import { computed, type ComputedRef, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import ReceiveCoinsToast from '../components/ReceiveCoinsToast.vue';
import { onError } from '@/plugins/pinia-colada';

export interface ITaskDto {
    _id: string
    type: number
    completions: number
    reward: number
    created: string
    url: string
    data: {
        groupId: number
    }
    title: string,
    description: string,
    completed: boolean
}

export function useTask(id: Ref<string> | ComputedRef<string>) {
    const result = useTaskList();

    const data = computed(() => result.data.value?.find(task => task._id === id.value))

    return { ...result, data }

}

export function useTaskList() {
    const Api = useBackendApi();

    return useQuery({
        key: () => ['taskList'],
        query: () => Api.get<ITaskDto[]>('/earn/task'),
    });
}

interface IVariables {
    task: ITaskDto;
}

interface IUseTaskCompleteParams
    extends Partial<UseMutationOptions<unknown, unknown, unknown>> {
    redirectTo?: string;
}

export function useTaskComplete({
    redirectTo = '/',
    ...params
}: IUseTaskCompleteParams = {}) {
    const Api = useBackendApi();
    const queryCache = useQueryCache();
    const toast = useToastStore();
    const router = useRouter();

    return useMutation({
        mutation: (task: ITaskDto) => Api.post(`/earn/task/${task._id}/complete`),
        onSuccess: (_, task) => {
            queryCache.invalidateQueries({ key: ['user'] });
            queryCache.invalidateQueries({ key: ['taskList'] });

            toast.addToast({
                status: 'success',
                content: ReceiveCoinsToast,
                props: {
                    message: 'Task successfully completed',
                    amount: task.reward
                }
            });

            router.push({ path: redirectTo, replace: true });
        },
        onError,
        ...params,
    });
}
